import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import {
  PackagePlus, Plus, ChevronDown, ChevronRight, AlertTriangle,
  Truck, DollarSign, Image as ImageIcon, X as XIcon,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const todayLot = () => {
  const d = new Date();
  return `LOT-${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}`;
};

// Compress an uploaded image client-side to <=1.6MB JPEG (matches Item Master pattern).
const compressImage = (file, maxBytes = 1_600_000) => new Promise((resolve, reject) => {
  if (file.size > 10 * 1024 * 1024) {
    reject(new Error('Image larger than 10MB. Please pick a smaller file.'));
    return;
  }
  const reader = new FileReader();
  reader.onload = (e) => {
    const img = new Image();
    img.onload = () => {
      let q = 0.85; let scale = 1;
      const tryCompress = () => {
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const c = document.createElement('canvas');
        c.width = w; c.height = h;
        c.getContext('2d').drawImage(img, 0, 0, w, h);
        const out = c.toDataURL('image/jpeg', q);
        const sz = Math.ceil((out.length - 'data:image/jpeg;base64,'.length) * 0.75);
        if (sz <= maxBytes || (q < 0.4 && scale < 0.4)) return resolve(out);
        if (q > 0.55) q -= 0.15; else { scale *= 0.85; q = 0.7; }
        tryCompress();
      };
      tryCompress();
    };
    img.onerror = () => reject(new Error('Could not read image.'));
    img.src = e.target.result;
  };
  reader.onerror = () => reject(new Error('File read failed.'));
  reader.readAsDataURL(file);
});

const RestockDialog = ({ open, onOpenChange, item, onDone, prefillExpectedOrder = null }) => {
  const { api, user } = useAuth();
  const [form, setForm] = useState({
    quantity: 1,
    batch_lot: '',
    vendor_id: '',
    location_id: '',
    notes: '',
    unit_cost: '',
    shipping_cost: '',
    currency: 'CAD',
    currency_other: '',
    fx_rate: '1.0',
    tariff_cost: '',
    customs_cost: '',
    brokerage_cost: '',
    freight_cost: '',
    other_barrier_cost: '',
    other_barrier_label: '',
    // iter78i — additional landed-cost components (CAD/receipt-currency)
    gst_recoverable_cost: '',
    gst_non_recoverable_cost: '',
    insurance_cost: '',
    handling_cost: '',
    other_costs: [], // [{label,value}]
    carrier: '',
    carrier_other: '',
    tracking_number: '',
    was_damaged: false,
    was_delayed: false,
    was_challenged: false,
    condition_notes: '',
    purchase_order_number: '',
    expected_order_id: '',
  });
  // iter78i — per-field cost mode ('$' or '%'); affects all landed-cost element inputs
  const [costMode, setCostMode] = useState({});

  const flipMode = (key) => setCostMode((m) => ({ ...m, [key]: m[key] === '%' ? '$' : '%' }));
  const resolveCost = (key, gross) => {
    const raw = parseFloat(form[key]) || 0;
    return costMode[key] === '%' ? (gross * raw / 100) : raw;
  };
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [carriers, setCarriers] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [expectedOrders, setExpectedOrders] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [showCosts, setShowCosts] = useState(false);
  const [showCarrier, setShowCarrier] = useState(false);
  const [packingSlipImage, setPackingSlipImage] = useState(null); // base64
  const [imgBusy, setImgBusy] = useState(false);
  const fileRef = useRef(null);
  // inline new vendor
  const [newVendorOpen, setNewVendorOpen] = useState(false);
  const [newVendorName, setNewVendorName] = useState('');
  const [newVendorBusy, setNewVendorBusy] = useState(false);

  useEffect(() => {
    if (!open || !item) return;
    // iter78i — pre-fill from item.landed_cost_defaults when present
    const defs = item.landed_cost_defaults || {};
    const nextMode = {};
    const pull = (key) => {
      const d = defs[key];
      if (d && (d.mode === 'percent' || d.mode === 'flat')) {
        nextMode[key] = d.mode === 'percent' ? '%' : '$';
        return String(d.value ?? '');
      }
      return '';
    };
    const otherFromDefaults = Array.isArray(defs.other)
      ? defs.other.map((o) => ({ label: o.label || '', value: o.value ?? '', mode: o.mode === 'percent' ? '%' : '$' }))
      : [];
    setForm({
      quantity: 1,
      batch_lot: todayLot(),
      vendor_id: item.preferred_vendor_id || '',
      location_id: item.location_id || '',
      notes: '',
      unit_cost: '',
      shipping_cost: '',
      currency: 'CAD', currency_other: '', fx_rate: '1.0',
      tariff_cost: pull('tariff'),
      customs_cost: '',
      brokerage_cost: pull('brokerage'),
      freight_cost: '',
      other_barrier_cost: '', other_barrier_label: '',
      gst_recoverable_cost: pull('gst_recoverable'),
      gst_non_recoverable_cost: pull('gst_non_recoverable'),
      insurance_cost: pull('insurance'),
      handling_cost: pull('handling'),
      other_costs: otherFromDefaults,
      carrier: '', carrier_other: '', tracking_number: '',
      was_damaged: false, was_delayed: false, was_challenged: false,
      condition_notes: '',
      purchase_order_number: '',
      expected_order_id: '',
    });
    setCostMode(nextMode);
    setPackingSlipImage(null);
    setShowCosts(false);
    setShowCarrier(false);
    Promise.all([
      api().get('/vendors'),
      api().get('/locations'),
      api().get('/lookups/carriers').catch(() => ({ data: { carriers: [] } })),
      api().get('/lookups/currencies').catch(() => ({ data: { currencies: [] } })),
      api().get(`/expected-orders?status=pending`).catch(() => ({ data: [] })),
    ]).then(([v, l, c, cu, eo]) => {
      setVendors(v.data || []);
      setLocations(l.data || []);
      setCarriers((c.data && c.data.carriers) || []);
      setCurrencies((cu.data && cu.data.currencies) || ['USD', 'CAD', 'EUR', 'GBP', 'MXN']);
      setExpectedOrders((eo.data || []).filter(e => e.item_id === item.id));
      // iter50: auto-apply prefillExpectedOrder when provided
      if (prefillExpectedOrder) {
        setForm(f => ({
          ...f,
          expected_order_id: prefillExpectedOrder.id,
          quantity: prefillExpectedOrder.expected_quantity || f.quantity,
          vendor_id: prefillExpectedOrder.vendor_id || f.vendor_id,
          purchase_order_number: prefillExpectedOrder.purchase_order_number || f.purchase_order_number,
        }));
      }
    }).catch(() => {});
    // eslint-disable-next-line
  }, [open, item?.id]);

  if (!item) return null;

  // Live landed-cost preview (iter78i — supports flat $ or % of gross per element)
  const qty = parseFloat(form.quantity) || 0;
  const uc = parseFloat(form.unit_cost) || 0;
  const fx = parseFloat(form.fx_rate) || 1;
  const gross = qty * uc;
  const sc = resolveCost('shipping_cost', gross);
  const tc = resolveCost('tariff_cost', gross);
  const cc = resolveCost('customs_cost', gross);
  const bc = resolveCost('brokerage_cost', gross);
  const fc = resolveCost('freight_cost', gross);
  const ob = resolveCost('other_barrier_cost', gross);
  const gstNon = resolveCost('gst_non_recoverable_cost', gross);
  const gstRec = resolveCost('gst_recoverable_cost', gross);  // shown separately, not in landed
  const ins = resolveCost('insurance_cost', gross);
  const hnd = resolveCost('handling_cost', gross);
  const otherCostsResolved = (form.other_costs || []).map((o) => {
    const raw = parseFloat(o.value) || 0;
    return { label: o.label || '', value: o.mode === '%' ? (gross * raw / 100) : raw };
  });
  const otherTot = otherCostsResolved.reduce((s, o) => s + (parseFloat(o.value) || 0), 0);
  const barriersTot = sc + tc + cc + bc + fc + ob + gstNon + ins + hnd + otherTot;
  const landedUSD = (gross + barriersTot) * fx;
  const landedUnitUSD = qty > 0 ? landedUSD / qty : 0;

  const submit = async (e) => {
    e.preventDefault();
    if (!(qty > 0)) { toast.error('Quantity must be greater than 0'); return; }
    setSubmitting(true);
    try {
      const payload = {
        quantity: qty,
        batch_lot: form.batch_lot || null,
        vendor_id: form.vendor_id || null,
        location_id: form.location_id || null,
        notes: form.notes || null,
        unit_cost: form.unit_cost === '' ? null : parseFloat(form.unit_cost),
        shipping_cost: form.shipping_cost === '' ? null : sc,
        currency: form.currency === 'Other' ? 'Other' : form.currency,
        currency_other: form.currency === 'Other' ? (form.currency_other || null) : null,
        fx_rate: form.fx_rate === '' ? 1.0 : parseFloat(form.fx_rate),
        // iter78i — send resolved $ amounts (percent → dollar already applied)
        tariff_cost: tc,
        customs_cost: cc,
        brokerage_cost: bc,
        freight_cost: fc,
        other_barrier_cost: ob,
        other_barrier_label: form.other_barrier_label || null,
        gst_recoverable_cost: gstRec,
        gst_non_recoverable_cost: gstNon,
        insurance_cost: ins,
        handling_cost: hnd,
        other_costs: otherCostsResolved.filter((o) => o.label || o.value),
        carrier: form.carrier === 'Other' ? 'Other' : (form.carrier || null),
        carrier_other: form.carrier === 'Other' ? (form.carrier_other || null) : null,
        tracking_number: form.tracking_number || null,
        was_damaged: !!form.was_damaged,
        was_delayed: !!form.was_delayed,
        was_challenged: !!form.was_challenged,
        condition_notes: form.condition_notes || null,
        packing_slip_image: packingSlipImage || null,
        purchase_order_number: form.purchase_order_number || null,
        expected_order_id: form.expected_order_id || null,
      };
      const { data } = await api().post(`/items/${item.id}/restock`, payload);
      const landed = data.landed_unit_cost_usd ? ` · landed $${(data.landed_unit_cost_usd).toFixed(2)}/unit USD` : '';
      toast.success(`Restocked +${qty} ${item.base_uom}. New on-hand: ${data.new_quantity}${landed}`);
      onDone?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Restock failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImgBusy(true);
    try {
      const data = await compressImage(file);
      setPackingSlipImage(data);
      toast.success('Packing slip attached');
    } catch (err) {
      toast.error(err.message || 'Image upload failed');
    } finally {
      setImgBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleAddNewVendor = async () => {
    if (!newVendorName.trim()) { toast.error('Vendor name required'); return; }
    setNewVendorBusy(true);
    try {
      const { data } = await api().post('/vendors', {
        vendor_name: newVendorName.trim(),
        active_status: true,
      });
      setVendors(prev => [...prev, data]);
      setForm(f => ({ ...f, vendor_id: data.id }));
      toast.success(`Vendor "${data.vendor_name}" added — flagged for Risk Analysis review`);
      setNewVendorOpen(false);
      setNewVendorName('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create vendor');
    } finally {
      setNewVendorBusy(false);
    }
  };

  const applyExpectedOrder = (eo) => {
    setForm(f => ({
      ...f,
      expected_order_id: eo.id,
      quantity: eo.expected_quantity || f.quantity,
      vendor_id: eo.vendor_id || f.vendor_id,
      purchase_order_number: eo.purchase_order_number || f.purchase_order_number,
    }));
    toast.success(`Receiving against PO ${eo.purchase_order_number || eo.id.slice(0, 6)}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="restock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <PackagePlus className="w-5 h-5 text-emerald-600" /> Restock — {item.item_sku}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          {/* Item info */}
          <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm flex items-center justify-between">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-0.5">Item</div>
              <div className="text-slate-800 font-medium">{item.item_name}</div>
              <div className="text-xs text-slate-500">On hand: <span className="mono">{item.current_quantity} {item.base_uom}</span></div>
            </div>
            {expectedOrders.length > 0 && (
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-blue-700 mb-0.5">Open POs</div>
                {expectedOrders.slice(0, 3).map(eo => (
                  <button type="button" key={eo.id}
                    onClick={() => applyExpectedOrder(eo)}
                    className={`block text-xs mono px-2 py-0.5 rounded-sm border mb-0.5 ${form.expected_order_id === eo.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white border-blue-200 text-blue-700 hover:bg-blue-50'}`}
                    data-testid={`apply-expected-${eo.id}`}>
                    PO {eo.purchase_order_number || eo.id.slice(0, 6)} · {eo.expected_quantity}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Qty + Lot + PO */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-1.5">
              <Label className="text-xs">Quantity Received *</Label>
              <Input type="number" min={1} step="any" value={form.quantity}
                onChange={(e) => setForm({ ...form, quantity: e.target.value })}
                required className="rounded-sm mono h-9" data-testid="restock-quantity" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">Lot # / Batch</Label>
              <Input value={form.batch_lot}
                onChange={(e) => setForm({ ...form, batch_lot: e.target.value })}
                placeholder="LOT-20260208" className="rounded-sm mono h-9" data-testid="restock-batch-lot" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs">PO Number</Label>
              <Input value={form.purchase_order_number}
                onChange={(e) => setForm({ ...form, purchase_order_number: e.target.value })}
                placeholder="PO-12345" className="rounded-sm mono h-9" data-testid="restock-po-number" />
            </div>
          </div>

          {/* Vendor + new vendor inline */}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label className="text-xs">Vendor</Label>
              <Button type="button" variant="ghost" size="sm" className="h-6 text-xs"
                onClick={() => setNewVendorOpen(!newVendorOpen)} data-testid="restock-new-vendor-toggle">
                <Plus className="w-3 h-3 mr-1" /> New vendor
              </Button>
            </div>
            <Select value={form.vendor_id || 'none'}
              onValueChange={(v) => setForm({ ...form, vendor_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="rounded-sm h-9" data-testid="restock-vendor-select">
                <SelectValue placeholder="Select vendor" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {vendors.map(v => <SelectItem key={v.id} value={v.id}>{v.vendor_name}</SelectItem>)}
              </SelectContent>
            </Select>
            {newVendorOpen && (
              <div className="flex items-center gap-2 mt-1 p-2 bg-blue-50 border border-blue-200 rounded-sm">
                <Input value={newVendorName} onChange={(e) => setNewVendorName(e.target.value)}
                  placeholder="New vendor name" className="rounded-sm h-8 text-xs" data-testid="restock-new-vendor-name" />
                <Button type="button" size="sm" disabled={newVendorBusy}
                  onClick={handleAddNewVendor}
                  className="bg-blue-600 hover:bg-blue-700 h-8 text-xs" data-testid="restock-new-vendor-save">
                  {newVendorBusy ? '…' : 'Add'}
                </Button>
              </div>
            )}
          </div>

          {/* Location */}
          <div className="space-y-1.5">
            <Label className="text-xs">Storage Location</Label>
            <Select value={form.location_id || 'none'}
              onValueChange={(v) => setForm({ ...form, location_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="rounded-sm h-9" data-testid="restock-location-select">
                <SelectValue placeholder="Select location" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {locations.map(l => (
                  <SelectItem key={l.id} value={l.id}>{l.location_name} / {l.bin_id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* COSTS — collapsible */}
          <button type="button" onClick={() => setShowCosts(!showCosts)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-700 w-full p-2 bg-slate-50 border border-slate-200 rounded-sm hover:bg-slate-100"
            data-testid="restock-costs-toggle">
            {showCosts ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <DollarSign className="w-4 h-4 text-emerald-600" />
            Costs · Currency · Tariffs / Barriers
            {(uc > 0 || barriersTot > 0) && (
              <Badge className="ml-auto bg-emerald-600 text-[10px]" data-testid="restock-landed-badge">
                Landed ${landedUnitUSD.toFixed(2)} USD/unit
              </Badge>
            )}
          </button>
          {showCosts && (
            <div className="space-y-3 border border-slate-200 rounded-sm p-3 bg-white">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Currency</Label>
                  <Select value={form.currency} onValueChange={(v) => setForm({ ...form, currency: v })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="restock-currency-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.currency === 'Other' && (
                  <div className="space-y-1">
                    <Label className="text-xs">Currency code</Label>
                    <Input value={form.currency_other} onChange={(e) => setForm({ ...form, currency_other: e.target.value })}
                      placeholder="e.g. INR" className="rounded-sm mono h-8 text-xs" data-testid="restock-currency-other" />
                  </div>
                )}
                <div className="space-y-1">
                  <Label className="text-xs">FX → USD</Label>
                  <Input type="number" step="0.0001" value={form.fx_rate}
                    onChange={(e) => setForm({ ...form, fx_rate: e.target.value })}
                    className="rounded-sm mono h-8 text-xs" data-testid="restock-fx-rate" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Unit Cost <span className="text-slate-400">(in {form.currency === 'Other' ? (form.currency_other || '?') : form.currency})</span></Label>
                  <Input type="number" step="0.01" placeholder="0.00" value={form.unit_cost}
                    onChange={(e) => setForm({ ...form, unit_cost: e.target.value })}
                    className="rounded-sm mono h-8 text-xs" data-testid="restock-unit-cost" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Shipping (vendor invoice)</Label>
                  <Input type="number" step="0.01" placeholder="0.00" value={form.shipping_cost}
                    onChange={(e) => setForm({ ...form, shipping_cost: e.target.value })}
                    className="rounded-sm mono h-8 text-xs" data-testid="restock-shipping-cost" />
                </div>
              </div>
              <div className="border-t border-slate-100 pt-2 mt-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Landed cost elements <span className="text-slate-400 normal-case">(toggle <span className="font-bold">$/%</span> per field; % is of goods value)</span></div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    ['tariff_cost', 'Tariffs', 'restock-tariff'],
                    ['customs_cost', 'Customs / Duty', 'restock-customs'],
                    ['brokerage_cost', 'Brokerage', 'restock-brokerage'],
                    ['freight_cost', 'Freight (carrier)', 'restock-freight'],
                    ['gst_recoverable_cost', 'GST (recoverable)', 'restock-gst-rec'],
                    ['gst_non_recoverable_cost', 'GST (non-rec.)', 'restock-gst-non'],
                    ['insurance_cost', 'Insurance', 'restock-insurance'],
                    ['handling_cost', 'Handling', 'restock-handling'],
                  ].map(([k, label, tid]) => {
                    const isPct = costMode[k] === '%';
                    const resolved = resolveCost(k, gross);
                    return (
                      <div key={k} className="space-y-0.5">
                        <Label className="text-[10px] text-slate-500 flex items-center gap-1">
                          {label}
                          {k === 'gst_recoverable_cost' && <span className="text-[9px] italic text-blue-600">(excl)</span>}
                        </Label>
                        <div className="flex gap-1">
                          <Input type="number" step="0.01" placeholder={isPct ? '%' : '0.00'} value={form[k]}
                            onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                            className="rounded-sm mono h-7 text-xs flex-1" data-testid={tid} />
                          <button type="button" onClick={() => flipMode(k)}
                            className={`px-1.5 h-7 text-[10px] rounded-sm border font-mono font-bold ${isPct ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'}`}
                            data-testid={`${tid}-mode`}
                            title={isPct ? `Percent of goods → $${resolved.toFixed(2)}` : 'Click to enter as % of goods value'}>
                            {isPct ? '%' : '$'}
                          </button>
                        </div>
                        {isPct && form[k] && (
                          <p className="text-[9px] text-emerald-700 mono">= ${resolved.toFixed(2)}</p>
                        )}
                      </div>
                    );
                  })}
                </div>

                {/* Legacy "other barrier" single-row (kept for compatibility) */}
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <div className="space-y-0.5">
                    <Label className="text-[10px] text-slate-500">Other label</Label>
                    <Input value={form.other_barrier_label} onChange={(e) => setForm({ ...form, other_barrier_label: e.target.value })}
                      placeholder="e.g. HST, VAT" className="rounded-sm h-7 text-xs" data-testid="restock-other-barrier-label" />
                  </div>
                  <div className="space-y-0.5">
                    <Label className="text-[10px] text-slate-500">Other amount</Label>
                    <div className="flex gap-1">
                      <Input type="number" step="0.01" placeholder="0.00" value={form.other_barrier_cost}
                        onChange={(e) => setForm({ ...form, other_barrier_cost: e.target.value })}
                        className="rounded-sm mono h-7 text-xs flex-1" data-testid="restock-other-barrier-cost" />
                      <button type="button" onClick={() => flipMode('other_barrier_cost')}
                        className={`px-1.5 h-7 text-[10px] rounded-sm border font-mono font-bold ${costMode.other_barrier_cost === '%' ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-300'}`}
                        data-testid="restock-other-barrier-mode">
                        {costMode.other_barrier_cost === '%' ? '%' : '$'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* iter78i — Repeatable "Other costs" list */}
                <div className="mt-2 border-t border-slate-100 pt-2">
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-[10px] text-slate-500 uppercase tracking-wider">Additional cost lines</Label>
                    <button type="button" onClick={() => setForm({ ...form, other_costs: [...(form.other_costs || []), { label: '', value: '', mode: '$' }] })}
                      className="text-[10px] px-2 h-6 rounded-sm border border-slate-300 hover:bg-slate-50" data-testid="restock-other-add">
                      + Add line
                    </button>
                  </div>
                  {(form.other_costs || []).map((row, idx) => {
                    const raw = parseFloat(row.value) || 0;
                    const isPct = row.mode === '%';
                    const dollar = isPct ? (gross * raw / 100) : raw;
                    const update = (patch) => {
                      const next = [...form.other_costs];
                      next[idx] = { ...next[idx], ...patch };
                      setForm({ ...form, other_costs: next });
                    };
                    return (
                      <div key={idx} className="grid grid-cols-[1fr,90px,40px,28px] gap-1 mb-1">
                        <Input value={row.label} placeholder="Label (e.g. Storage fee)" onChange={(e) => update({ label: e.target.value })}
                          className="rounded-sm h-7 text-xs" data-testid={`restock-other-line-label-${idx}`} />
                        <Input type="number" step="0.01" value={row.value} placeholder={isPct ? '%' : '0.00'} onChange={(e) => update({ value: e.target.value })}
                          className="rounded-sm mono h-7 text-xs" data-testid={`restock-other-line-value-${idx}`} />
                        <button type="button" onClick={() => update({ mode: isPct ? '$' : '%' })}
                          className={`px-1 h-7 text-[10px] rounded-sm border font-mono font-bold ${isPct ? 'bg-orange-500 text-white border-orange-500' : 'bg-white text-slate-600 border-slate-300'}`}
                          data-testid={`restock-other-line-mode-${idx}`}>
                          {isPct ? '%' : '$'}
                        </button>
                        <button type="button" onClick={() => setForm({ ...form, other_costs: form.other_costs.filter((_, i) => i !== idx) })}
                          className="text-red-500 hover:text-red-700 text-[14px]" title="Remove" data-testid={`restock-other-line-remove-${idx}`}>
                          ×
                        </button>
                        {isPct && row.value && <p className="text-[9px] text-emerald-700 mono col-span-4 -mt-1">= ${dollar.toFixed(2)}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
              {/* Live landed-cost summary */}
              {(qty > 0 && (uc > 0 || barriersTot > 0)) && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-sm p-2 text-xs space-y-0.5" data-testid="restock-landed-summary">
                  <div className="flex justify-between"><span className="text-slate-600">Gross ({qty} × {uc.toFixed(2)})</span><span className="mono">{(gross).toFixed(2)} {form.currency}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">+ Barriers</span><span className="mono">{barriersTot.toFixed(2)} {form.currency}</span></div>
                  <div className="flex justify-between text-slate-500 italic"><span>× FX {fx}</span><span className="mono">{(landedUSD).toFixed(2)} USD</span></div>
                  <div className="flex justify-between font-semibold text-emerald-700 border-t border-emerald-200 pt-0.5 mt-0.5"><span>Landed Unit Cost (USD)</span><span className="mono">${(landedUnitUSD).toFixed(4)}</span></div>
                </div>
              )}
            </div>
          )}

          {/* CARRIER / CONDITION */}
          <button type="button" onClick={() => setShowCarrier(!showCarrier)}
            className="flex items-center gap-2 text-sm font-semibold text-slate-700 w-full p-2 bg-slate-50 border border-slate-200 rounded-sm hover:bg-slate-100"
            data-testid="restock-carrier-toggle">
            {showCarrier ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <Truck className="w-4 h-4 text-blue-600" />
            Carrier · Tracking · Condition
            {(form.was_damaged || form.was_delayed || form.was_challenged) && (
              <Badge className="ml-auto bg-amber-500 text-[10px]" data-testid="restock-condition-badge">
                <AlertTriangle className="w-2.5 h-2.5 mr-0.5" /> Issue flagged
              </Badge>
            )}
          </button>
          {showCarrier && (
            <div className="space-y-3 border border-slate-200 rounded-sm p-3 bg-white">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs">Carrier</Label>
                  <Select value={form.carrier || 'none'} onValueChange={(v) => setForm({ ...form, carrier: v === 'none' ? '' : v })}>
                    <SelectTrigger className="rounded-sm h-8 text-xs" data-testid="restock-carrier-select">
                      <SelectValue placeholder="Select carrier" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {carriers.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                {form.carrier === 'Other' && (
                  <div className="space-y-1">
                    <Label className="text-xs">Carrier name</Label>
                    <Input value={form.carrier_other} onChange={(e) => setForm({ ...form, carrier_other: e.target.value })}
                      placeholder="e.g. Local courier" className="rounded-sm h-8 text-xs" data-testid="restock-carrier-other" />
                  </div>
                )}
                <div className="space-y-1 col-span-2">
                  <Label className="text-xs">Tracking #</Label>
                  <Input value={form.tracking_number} onChange={(e) => setForm({ ...form, tracking_number: e.target.value })}
                    placeholder="1Z..." className="rounded-sm mono h-8 text-xs" data-testid="restock-tracking" />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100">
                {[
                  ['was_damaged', 'Damaged'],
                  ['was_delayed', 'Delayed'],
                  ['was_challenged', 'Challenged (customs/inspection)'],
                ].map(([k, lbl]) => (
                  <label key={k} className="flex items-center gap-1.5 text-xs cursor-pointer p-1.5 rounded-sm hover:bg-slate-50"
                    data-testid={`restock-flag-${k}`}>
                    <Switch checked={!!form[k]} onCheckedChange={(v) => setForm({ ...form, [k]: !!v })} />
                    <span>{lbl}</span>
                  </label>
                ))}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Condition notes</Label>
                <Textarea value={form.condition_notes} onChange={(e) => setForm({ ...form, condition_notes: e.target.value })}
                  placeholder="Carton damage, customs hold reason, etc." className="rounded-sm text-xs" rows={2}
                  data-testid="restock-condition-notes" />
              </div>
            </div>
          )}

          {/* PACKING SLIP IMAGE */}
          <div className="space-y-1">
            <Label className="text-xs flex items-center gap-1.5">
              <ImageIcon className="w-3.5 h-3.5" /> Packing Slip / PO Photo <span className="text-slate-400">(optional, ≤10MB)</span>
            </Label>
            {packingSlipImage ? (
              <div className="relative inline-block">
                <img src={packingSlipImage} alt="Packing slip" className="max-h-40 rounded-sm border border-slate-200" />
                <Button type="button" size="sm" variant="ghost" onClick={() => setPackingSlipImage(null)}
                  className="absolute top-0 right-0 h-6 w-6 p-0 bg-white border border-slate-200" data-testid="restock-remove-image">
                  <XIcon className="w-3 h-3" />
                </Button>
              </div>
            ) : (
              <div>
                <input ref={fileRef} type="file" accept="image/*" capture="environment" onChange={handleImageUpload}
                  className="hidden" data-testid="restock-image-input" />
                <Button type="button" variant="outline" size="sm" disabled={imgBusy}
                  onClick={() => fileRef.current?.click()}
                  className="rounded-sm text-xs" data-testid="restock-image-btn">
                  <ImageIcon className="w-3.5 h-3.5 mr-1" /> {imgBusy ? 'Compressing…' : 'Take / upload photo'}
                </Button>
              </div>
            )}
          </div>

          {/* General notes */}
          <div className="space-y-1">
            <Label className="text-xs">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Anything else…" className="rounded-sm text-xs" rows={2} data-testid="restock-notes" />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm">Cancel</Button>
            <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 rounded-sm" data-testid="restock-submit-btn">
              <PackagePlus className="w-4 h-4 mr-2" /> {submitting ? 'Saving…' : 'Confirm Restock'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default RestockDialog;
